
import React, { useEffect, useMemo, useState } from 'react';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ManufacturerRow from '@/components/ManufacturerRow';
import { useToast } from '@/components/ui/use-toast';
import { useMarkets } from '@/hooks/useMarkets';
import { useManufacturers } from '@/hooks/useManufacturers';
import { supabase } from '@/integrations/supabase/client';

const ManufacturerOverview = () => {
  const { toast } = useToast();
  const { masterData, adData, atpData, refetchMaster } = useManufacturers();

  // Collect market-country string options, excluding any 'all'
  const uniqueMarkets = useMarkets(masterData, adData, atpData);

  // User-selected market (country code, must exist)
  const [selectedMarket, setSelectedMarket] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (
      (!selectedMarket || !uniqueMarkets.includes(selectedMarket)) &&
      uniqueMarkets.length
    ) {
      setSelectedMarket(uniqueMarkets[0]);
    }
  }, [uniqueMarkets, selectedMarket]);

  // Filter all sources by selected market only
  const filterByMarket = (data: any[] | undefined) =>
    (data || []).filter((item) => item.market === selectedMarket);

  const filteredMasterData = filterByMarket(masterData);
  const filteredAdData = filterByMarket(adData);
  const filteredAtpData = filterByMarket(atpData);

  // Build deduped list on maker_id per market
  const sortedManufacturers = useMemo(() => {
    const all = [
      ...(filteredMasterData || []).map(m => ({
        maker_id: m.maker_id,
        manufacturer_id: m.manufacturer_id,
        name: m.manufacturer_name,
        market: m.market,
        adObj: undefined,
        atpObj: undefined,
        masterObj: m,
      })),
      ...(filteredAdData || []).map(m => ({
        maker_id: m.maker_id,
        manufacturer_id: m.manufacturer_id,
        name: m.manufacturer_name,
        market: m.market,
        adObj: m,
        atpObj: undefined,
        masterObj: undefined,
      })),
      ...(filteredAtpData || []).map(m => ({
        maker_id: m.maker_id,
        manufacturer_id: m.manufacturer_id,
        name: m.manufacturer_name,
        market: m.market,
        adObj: undefined,
        atpObj: m,
        masterObj: undefined,
      })),
    ];
    // Map by (maker_id, market) pair
    const map = new Map();
    all.forEach((m) => {
      const key = `${m.maker_id}_${m.market}`;
      if (!map.has(key) && m.maker_id != null && m.market) map.set(key, m);
    });
    return Array.from(map.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [filteredMasterData, filteredAdData, filteredAtpData]);

  // Presence checks (per maker_id+market)
  const isInMaster = (makerId: number, market: string) =>
    !!filteredMasterData?.some(m => m.maker_id === makerId && m.market === market);

  // Handler for importing manufacturer
  const handleImport = async (
    manufacturer:
      | { manufacturer_name: string, manufacturer_id: number, maker_id?: number, market?: string }
      | undefined
  ) => {
    if (!manufacturer || !selectedMarket) return;
    const { error } = await supabase
      .from('vehicledb_master')
      .insert({
        manufacturer_name: manufacturer.manufacturer_name,
        manufacturer_id: manufacturer.manufacturer_id,
        maker_id: manufacturer.maker_id,
        activated: true,
        market: selectedMarket,
      });
    if (error) {
      toast({
        variant: "destructive",
        title: "Import failed",
        description: "Could not import manufacturer to master database",
      });
    } else {
      toast({
        title: "Success",
        description: `${manufacturer.manufacturer_name} was imported to master database`,
      });
      refetchMaster();
    }
  };

  // Remove from vehicle master for current market only
  const handleRemove = async (makerId: number, market: string) => {
    if (!makerId || !market) return;
    const { error } = await supabase
      .from('vehicledb_master')
      .delete()
      .eq('maker_id', makerId)
      .eq('market', market);
    if (error) {
      toast({
        variant: "destructive",
        title: "Removal failed",
        description: "Could not remove manufacturer from master database"
      });
    } else {
      toast({
        title: "Removed",
        description: `Manufacturer removed from master database.`,
      });
      refetchMaster();
    }
  };

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Manufacturer Overview</h1>
      <div className="mb-4">
        <Select value={selectedMarket} onValueChange={setSelectedMarket}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Select market" />
          </SelectTrigger>
          <SelectContent>
            {uniqueMarkets.map((market) => (
              <SelectItem key={market} value={market}>{market}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="bg-white rounded-lg shadow p-6">
        <Table>
          <TableCaption>Vehicle Manufacturers Overview</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>Manufacturer</TableHead>
              <TableHead>Master</TableHead>
              <TableHead>AD</TableHead>
              <TableHead>ATP</TableHead>
              <TableHead>Add to Tree</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedManufacturers.map((manufacturer) => {
              // In each market, check presence
              const masterPresent = isInMaster(manufacturer.maker_id, selectedMarket!);
              const adPresent = !!filteredAdData.find(m => m.maker_id === manufacturer.maker_id);
              const atpPresent = !!filteredAtpData.find(m => m.maker_id === manufacturer.maker_id);
              // Import source preference: AD > ATP
              const importSourceObj =
                adPresent
                  ? filteredAdData.find(m => m.maker_id === manufacturer.maker_id)
                  : atpPresent
                  ? filteredAtpData.find(m => m.maker_id === manufacturer.maker_id)
                  : undefined;

              return (
                <ManufacturerRow
                  key={manufacturer.maker_id + "_" + manufacturer.market}
                  manufacturer={manufacturer}
                  masterPresent={masterPresent}
                  adPresent={adPresent}
                  atpPresent={atpPresent}
                  onImport={handleImport}
                  onRemove={handleRemove}
                  importSourceObj={importSourceObj}
                />
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ManufacturerOverview;
